import { useImmer } from "use-immer";
import { useEffect, useState } from "react";
import ApiConnector from "../connector/ApiConnector";
import ActionGeneralConfig from "../component/adminPage/ActionGeneralConfig";
import Note from "../component/Note";
import GeneralConfigService from "../service/GeneralConfigService";
import { Link } from "react-router-dom";
import SuccessStatisticsCache from "../component/dashboardPage/SuccessStatisticsCache";
import ConfigEfficiencyWidget from "../component/dashboardPage/ConfigEfficiencyWidget";
import RuleImpactAnalysisWidget from "../component/dashboardPage/RuleImpactAnalysisWidget";
import VulnerablePanConfig from "../component/dashboardPage/VulnerablePanConfig";
import EwPayLimitLookup from "../component/dashboardPage/EwPayLimitLookup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFloppyDisk,
  faCircleQuestion,
} from "@fortawesome/free-regular-svg-icons";

import { useTableModel } from "../service/TableModelUtilService";
import { normalizeRowsForSignature } from "../service/NormalizeForSignatureUtilService";

const Component = () => {
  const [generalConfig, updateGeneralConfig] = useImmer(null);
  const [vulnerablePans, updateVulnerablePans] = useImmer([]);
  const [baselineVulnerablePans, updateBaselineVulnerablePans] = useImmer([]);
  const [statistics, updateStatistics] = useImmer({
    authTransactionCount: 0,
    declinedAuthTransactionCount: 0,
    declinedBlockCardAuthTransactionCount: 0,
    suppressedAuthTransactionCount: 0,
    fraudAuthTransactionCount: 0,
  });
  const [successStatisticsCache, updateSuccessStatisticsCache] = useImmer(null);
  const [last24hoursInfoOpen, setLast24hoursInfoOpen] = useState(false);
  const [successRateInfoOpen, setSuccessRateInfoOpen] = useState(false);
  const [configEfficiencyInfoOpen, setConfigEfficiencyInfoOpen] =
    useState(false);
  const [ruleImpactInfoOpen, setRuleImpactInfoOpen] = useState(false);

  const handleInfoIconClick = (detailsId, isOpen, setOpen) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!isOpen);
  };

  const {
    canSave: canSaveGeneralConfig,
    setRows: setModelRows,
    setBaselineFromRows,
  } = useTableModel({
    initialRows: [],
    columns: [],
    keyField: "_key",
    initialSortBy: null,
    initialSortDir: "asc",
    normalizeForSignature: normalizeRowsForSignature,
  });

  const hasVulnerablePansChanged = () => {
    if (vulnerablePans.length !== baselineVulnerablePans.length) return true;
    const currentIds = vulnerablePans
      .filter((vp) => !vp.isNew)
      .map((vp) => vp.id)
      .sort();
    const baselineIds = baselineVulnerablePans.map((vp) => vp.id).sort();
    if (JSON.stringify(currentIds) !== JSON.stringify(baselineIds)) return true;
    return vulnerablePans.some((vp) => vp.isNew);
  };

  const canSave = canSaveGeneralConfig || hasVulnerablePansChanged();

  useEffect(() => {
    let mounted = true;

    async function load() {
      const t = await ApiConnector.getLatestGeneralConfig();
      if (!mounted) return;
      updateGeneralConfig(t);
      setModelRows([t]);
      setBaselineFromRows([t]);

      const vps = await ApiConnector.getVulnerablePans();
      if (!mounted) return;
      if (vps) {
        updateVulnerablePans(vps);
        updateBaselineVulnerablePans(vps);
      }

      await loadStatistics();

      const cache = await ApiConnector.getLatestCache("SuccessStatisticsCache");
      if (mounted && cache?.content)
        updateSuccessStatisticsCache(cache.content);
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const createGeneralConfig = async () => {
    if (!canSave || !generalConfig) return;

    const ok = window.confirm(
      "New general config will be created and set as active.",
    );
    if (!ok) return;

    const newConfig = await GeneralConfigService.createGeneralConfig(
      generalConfig,
      generalConfig.id,
    );
    if (!newConfig) return;

    updateGeneralConfig(newConfig);
    setModelRows([newConfig]);
    setBaselineFromRows([newConfig]);

    const baselineIds = baselineVulnerablePans.map((vp) => vp.id);
    const currentIds = vulnerablePans
      .filter((vp) => !vp.isNew)
      .map((vp) => vp.id);

    for (const id of baselineIds) {
      if (!currentIds.includes(id)) {
        await ApiConnector.deleteVulnerablePan(id);
      }
    }

    for (const vp of vulnerablePans) {
      if (vp.isNew && vp.pan) {
        await ApiConnector.createVulnerablePan(
          vp.pan,
          vp.merchantId,
          vp.expirationMinutes,
        );
      }
    }

    const vps = await ApiConnector.getVulnerablePans();
    if (vps) {
      updateVulnerablePans(vps);
      updateBaselineVulnerablePans(vps);
    }
  };

  const loadStatistics = async () => {
    let last24HoursTimestamp = new Date();
    last24HoursTimestamp.setHours(last24HoursTimestamp.getHours() - 24);

    const authTransaction = await ApiConnector.getTransactions({
      trnDateMin: last24HoursTimestamp,
      transactionTypes: ["A"],
      displayCount: true,
      pageSize: 0,
    });
    const declinedAuthTransaction = await ApiConnector.getTransactions({
      trnDateMin: last24HoursTimestamp,
      transactionTypes: ["A"],
      statuses: ["DECLINED"],
      displayCount: true,
      pageSize: 0,
    });
    const declinedBlockCardAuthTransaction = await ApiConnector.getTransactions(
      {
        trnDateMin: last24HoursTimestamp,
        transactionTypes: ["A"],
        statuses: ["DECLINED_BLOCK_CARD"],
        displayCount: true,
        pageSize: 0,
      },
    );
    const suppressedAuthTransaction = await ApiConnector.getTransactions({
      trnDateMin: last24HoursTimestamp,
      transactionTypes: ["A"],
      detailStatuses: ["APPROVED_BY_PAN_SUPPRESS"],
      displayCount: true,
      pageSize: 0,
    });
    const fraudAuthTransaction = await ApiConnector.getTransactions({
      trnDateMin: last24HoursTimestamp,
      transactionTypes: ["A"],
      isFraud: true,
      displayCount: true,
      pageSize: 0,
    });

    updateStatistics((d) => {
      d.authTransactionCount = authTransaction.count;
      d.declinedAuthTransactionCount = declinedAuthTransaction.count;
      d.declinedBlockCardAuthTransactionCount =
        declinedBlockCardAuthTransaction.count;
      d.suppressedAuthTransactionCount = suppressedAuthTransaction.count;
      d.fraudAuthTransactionCount = fraudAuthTransaction.count;
    });
  };

  return (
    generalConfig && (
      <>
        <header className="subheader header-with-save">
          <h1 className="header-title">Dashboard</h1>
          <span className="header-save">
            <FontAwesomeIcon
              icon={faFloppyDisk}
              size="2xl"
              className="icon-clickable"
              onClick={canSave ? createGeneralConfig : undefined}
              title={canSave ? "Save changes" : "No changes to save"}
              style={{
                opacity: canSave ? 1 : 0.4,
                cursor: canSave ? "pointer" : "not-allowed",
                pointerEvents: canSave ? "auto" : "none",
              }}
              aria-disabled={!canSave}
            />
          </span>
        </header>

        <main>
          <div className="row">
            <div className="col">
              <div className="row">
                <div className="col">
                  <h2>Statistics</h2>
                </div>
              </div>

              <h3>
                Last 24 hours{" "}
                <span className="section-header-icons">
                  <Note
                    noteTargetPath="dashboard.last24hours"
                    noteTargetId={generalConfig.id}
                  />
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className="info-icon"
                    onClick={handleInfoIconClick(
                      "last24hours-details",
                      last24hoursInfoOpen,
                      setLast24hoursInfoOpen,
                    )}
                    title={last24hoursInfoOpen ? "Hide info" : "Show info"}
                  />
                </span>
              </h3>

              {last24hoursInfoOpen && (
                <div className="info-box">
                  <p>
                    <strong>Displays performance metrics</strong> and processed
                    authorizations over the past 24 hours. Each row represents a
                    single system action by Pablo, rather than a unique card.
                  </p>
                </div>
              )}

              <table className="config-table">
                <thead>
                  <tr>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Authorizations</td>
                    <td>{statistics.authTransactionCount}</td>
                  </tr>
                  <tr>
                    <td>Declined</td>
                    <td>{statistics.declinedAuthTransactionCount}</td>
                  </tr>
                  <tr>
                    <td>Declined - blocked card</td>
                    <td>{statistics.declinedBlockCardAuthTransactionCount}</td>
                  </tr>
                  <tr>
                    <td>Pan suppressed</td>
                    <td>{statistics.suppressedAuthTransactionCount}</td>
                  </tr>
                  <tr>
                    <td>Confirmed frauds</td>
                    <td>{statistics.fraudAuthTransactionCount}</td>
                  </tr>
                </tbody>
              </table>

              <h3>
                Success rate - Last 30 days{" "}
                <span className="section-header-icons">
                  <Note
                    noteTargetPath="dashboard.successRate"
                    noteTargetId={generalConfig.id}
                  />
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className="info-icon"
                    onClick={handleInfoIconClick(
                      "successRate-details",
                      successRateInfoOpen,
                      setSuccessRateInfoOpen,
                    )}
                    title={successRateInfoOpen ? "Hide info" : "Show info"}
                  />
                </span>
              </h3>

              {successRateInfoOpen && (
                <div className="info-box">
                  <p>
                    <strong>Tracks Pablo's efficiency</strong> over the last 30
                    days by comparing correct fraud detections (True Positives)
                    against incorrect blocks of legitimate users (False
                    Positives). It specifically highlights "Effective" metrics
                    to distinguish between actual financial damage caused versus
                    damage successfully prevented by the system.
                  </p>
                </div>
              )}

              <SuccessStatisticsCache
                successStatistic={
                  successStatisticsCache
                    ? successStatisticsCache.successStatistics[0]
                    : null
                }
              ></SuccessStatisticsCache>
              <Link to="/dashboard/success-statistics">View detail</Link>

              <h3>
                Config Efficiency{" "}
                <span className="section-header-icons">
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className="info-icon"
                    onClick={handleInfoIconClick(
                      "configEfficiency-details",
                      configEfficiencyInfoOpen,
                      setConfigEfficiencyInfoOpen,
                    )}
                    title={configEfficiencyInfoOpen ? "Hide info" : "Show info"}
                  />
                </span>
              </h3>

              {configEfficiencyInfoOpen && (
                <div className="info-box">
                  <p>
                    Compares the current config's effective TP/FP ratio against
                    the best-performing configs from the last 90 days, grouped
                    by 30-day periods.
                  </p>
                </div>
              )}

              <ConfigEfficiencyWidget />

              <h3>
                Rule Impact Analysis{" "}
                <span className="section-header-icons">
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className="info-icon"
                    onClick={handleInfoIconClick(
                      "ruleImpact-details",
                      ruleImpactInfoOpen,
                      setRuleImpactInfoOpen,
                    )}
                    title={ruleImpactInfoOpen ? "Hide info" : "Show info"}
                  />
                </span>
              </h3>

              {ruleImpactInfoOpen && (
                <div className="info-box">
                  <p>
                    Shows which rules contributed most to fraud detection in the
                    selected period. Top 10 rules are bold.
                  </p>
                  <p>
                    <strong>Disc.</strong> (Discrimination ratio) is the key
                    metric — it compares how often a rule scores on fraud
                    transactions vs. legitimate ones. A ratio above 1.2 (green)
                    means the rule is a strong fraud signal. Around 1.0 (orange)
                    means the rule fires on both equally — low value. Below 1.0
                    (red) means the rule actually scores more on legitimate
                    transactions than fraud.
                  </p>
                  <p>
                    Use <strong>Apply rules</strong> to adopt the rule weights
                    from the best-performing configuration found in the selected
                    period — this will create a new active configuration with
                    those weights applied to the current setup. Only rules with
                    a good Disc. ratio are pre-selected.
                  </p>
                </div>
              )}

              <RuleImpactAnalysisWidget />
            </div>

            <div className="col">
              <div className="row">
                <div className="col">
                  <h2>Actions</h2>
                </div>
                <div className="col">
                  <h2 className="float-right">
                    <Note
                      noteTargetPath="generalConfig"
                      noteTargetId={generalConfig.id}
                    />
                  </h2>
                </div>
              </div>

              <ActionGeneralConfig
                config={generalConfig}
                onChange={(config) => {
                  updateGeneralConfig(config);
                  setModelRows([config]);
                }}
              ></ActionGeneralConfig>

              <VulnerablePanConfig
                vulnerablePans={vulnerablePans}
                onChange={(vps) => {
                  updateVulnerablePans(vps);
                }}
              />

              <h3>EW Pay — Customer Limit Lookup</h3>
              <EwPayLimitLookup />
            </div>
          </div>
        </main>
      </>
    )
  );
};

export default Component;
